import { useCallback, useMemo } from 'react';
import { WidgetType, WIDGET_CHART, WIDGET_INDICATOR, WIDGET_OTHER, WIDGET_TABULAR } from 'domain/entity/widget';
import { dispatchCurrentWidgetConfiguration, useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { PanelWidgetType } from 'adapter/in/common/widget/WidgetTypePreset/types';
import { getWidgetTypeFieldGroupItem } from 'adapter/in/common/utils/getWidgetTypeFieldGroupItem';

export type WidgetTypeFieldGroupItem = { value: WidgetType; id: WidgetType; tooltip: string; icon: string; isDisabled: boolean };
export type OptionsAndSelectedValue = { options: WidgetTypeFieldGroupItem[]; selectedValue: WidgetType };

export const sortedIndicators = (availableWidgetTypes: string[], isFeaturePresetSelected: boolean) => [
    getWidgetTypeFieldGroupItem(WIDGET_INDICATOR.SINGLE_KPI, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_INDICATOR.GAUGE, availableWidgetTypes, isFeaturePresetSelected),
    /*     getWidgetTypeFieldGroupItem(WIDGET_INDICATOR.PERCENTAGE, widgets), */
];

export const sortedCharts = (availableWidgetTypes: string[], isFeaturePresetSelected: boolean) => [
    getWidgetTypeFieldGroupItem(WIDGET_CHART.LINE, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_CHART.BAR, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_CHART.STACKED_BAR, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_CHART.PIE, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_CHART.HEATMAP, availableWidgetTypes, isFeaturePresetSelected),
];

export const sortedTabulars = (availableWidgetTypes: string[], isFeaturePresetSelected: boolean) => [
    getWidgetTypeFieldGroupItem(WIDGET_TABULAR.TABLE, availableWidgetTypes, isFeaturePresetSelected),
];

export const sortedOthers = (availableWidgetTypes: string[], isFeaturePresetSelected: boolean) => [
    getWidgetTypeFieldGroupItem(WIDGET_OTHER.TEXT, availableWidgetTypes, isFeaturePresetSelected),
    getWidgetTypeFieldGroupItem(WIDGET_OTHER.IMAGE, availableWidgetTypes, isFeaturePresetSelected),
];

export const mapWidgetsToMenu = (
    presetWidgetTypes: string[],
    activeWidgetType: WidgetType
): { options: WidgetTypeFieldGroupItem[]; selectedValue: WidgetType } => {
    const options = sortedIndicators(presetWidgetTypes, true)
        .concat(sortedCharts(presetWidgetTypes, true))
        .concat(sortedTabulars(presetWidgetTypes, true))
        .concat(sortedOthers(presetWidgetTypes, true))
        .filter((fieldgroupItem) => !fieldgroupItem.isDisabled);
    const selectedValue = activeWidgetType;

    return { options, selectedValue };
};

const useWidgetTypePresetContainer = (): {
    presetWidgetTypes: OptionsAndSelectedValue;
    onWidgetSelect: (widget: PanelWidgetType) => void;
} => {
    const { setWidgetType } = dispatchCurrentWidgetConfiguration();
    const { widgetType, availableWidgetTypes } = useCurrentWidgetConfiguration();
    const handleOnWidgetSelect = useCallback((widget: PanelWidgetType) => setWidgetType(widget.value), []);

    const presetWidgetTypes = useMemo(() => mapWidgetsToMenu(availableWidgetTypes, widgetType), [availableWidgetTypes, widgetType]);

    return {
        presetWidgetTypes,
        onWidgetSelect: handleOnWidgetSelect,
    };
};

export default useWidgetTypePresetContainer;
